import theme from '../../theme';
import { useState, useEffect } from 'react';
import QuizScore from './QuizScore';

// The answer key no longer ships with the questions -- the server releases one
// answer at a time, for a paper it actually dealt, against the practice token
// (ADR-0011). So marking is a round trip rather than a local comparison.
function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [token, setToken] = useState(null);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // { isCorrect, correctOption }
  const [checking, setChecking] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const showResult = feedback !== null;

  const fetchQuestions = async () => {
    setLoading(true);
    setError(false);
    setScore(0);
    setCurrent(0);
    setSelected(null);
    setFeedback(null);
    setChecking(false);
    setFinished(false);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/quiz/random?count=10`,
        { signal: AbortSignal.timeout(15000) }
      );
      if (!res.ok) throw new Error(`Quiz API returned ${res.status}`);
      const data = await res.json();
      if (!data || !Array.isArray(data.questions) || !data.practiceToken) {
        throw new Error('Quiz API returned an unexpected response shape');
      }
      setQuestions(data.questions);
      setToken(data.practiceToken);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleSelect = async (index) => {
    if (showResult || checking) return;
    setSelected(index);
    setChecking(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quiz/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          practiceToken: token,
          questionId: questions[current]._id,
          answer: index,
        }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`Check API returned ${res.status}`);
      const result = await res.json();
      setFeedback({ isCorrect: result.isCorrect, correctOption: result.correctOption });
      if (result.isCorrect) setScore(s => s + 1);
    } catch (err) {
      // Marking failed, so the selection is not scored either way -- clearing
      // it lets the student answer again rather than stranding them on a
      // question with no verdict and a dead Next button.
      console.error('Failed to check answer:', err);
      setSelected(null);
    }
    setChecking(false);
  };

  const handleNext = () => {
    if (current + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrent(c => c + 1);
      setSelected(null);
      setFeedback(null);
    }
  };

  if (loading) return (
    <p style={{ textAlign: 'center', color: theme.textMuted, fontSize: '13px', padding: '1rem' }}>
      Loading questions...
    </p>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
      <p style={{ color: theme.error, fontSize: '13px', marginBottom: '1rem' }}>
        Failed to load questions. Please check your connection.
      </p>
      <button onClick={fetchQuestions} className="btn-primary">
        Try again
      </button>
    </div>
  );

  if (finished) return (
    <QuizScore
      score={score}
      total={questions.length}
      onRestart={fetchQuestions}
    />
  );

  const q = questions[current];
  if (!q) return (
    <p style={{ textAlign: 'center', color: theme.textMuted, fontSize: '13px' }}>
      Loading questions...
    </p>
  );

  return (
    <div style={{
      maxWidth: '520px',
      margin: '0 auto',
      padding: '1.2rem 1.5rem',
      background: theme.bgCard,
      borderRadius: '4px',
      border: '1px solid rgba(184, 149, 90, 0.3)',
      color: theme.textPrimary,
    }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem',
      }}>
        <span style={{ fontSize: '14px', color: theme.textMuted }}>
          Question <strong style={{ color: theme.gold }}>{current + 1}</strong> of {questions.length}
        </span>
        <span style={{
          fontSize: '14px',
          background: theme.bgDark,
          color: theme.textPrimary,
          padding: '3px 9px',
          borderRadius: '4px',
          border: '1px solid rgba(184, 149, 90, 0.2)',
          fontWeight: '500',
        }}>
          Score: {score}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        background: theme.bgDark,
        borderRadius: '2px',
        height: '3px',
        marginBottom: '1rem',
      }}>
        <div style={{
          background: theme.gold,
          height: '3px',
          borderRadius: '2px',
          width: `${((current + 1) / questions.length) * 100}%`,
          transition: 'width 0.3s',
        }} />
      </div>

      {/* Question */}
      <p style={{
        fontSize: '19px',
        fontWeight: '600',
        color: theme.textPrimary,
        marginBottom: '1rem',
        lineHeight: '1.4',
      }}>
        {q.question}
      </p>

      {/* Hint */}
      {selected === null && (
        <p style={{
          fontSize: '13px',
          color: theme.textMuted,
          marginBottom: '0.5rem',
          fontStyle: 'italic',
        }}>
          ↓ Select an answer to continue
        </p>
      )}

      {/* Options */}
      {q.options.map((option, index) => (
        <div
          key={index}
          onClick={() => handleSelect(index)}
          style={{
            padding: '0.65rem 0.95rem',
            marginBottom: '0.4rem',
            border: `1.5px solid ${selected === index ? theme.gold : 'rgba(184, 149, 90, 0.2)'}`,
            borderRadius: '4px',
            cursor: selected !== null || checking ? 'default' : 'pointer',
            background: selected === index ? theme.bgDark : 'transparent',
            fontSize: '15px',
            color: selected === index ? theme.gold : theme.textPrimary,
            fontWeight: selected === index ? '500' : '400',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: selected === index ? theme.gold : 'rgba(184, 149, 90, 0.2)',
            color: selected === index ? theme.bgDark : theme.textMuted,
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontWeight: '600',
          }}>
            {String.fromCharCode(65 + index)}
          </span>
          {option}
        </div>
      ))}

      {/* Result feedback */}
      {showResult && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '4px',
          background: feedback.isCorrect ? 'rgba(58, 170, 120, 0.15)' : 'rgba(214, 63, 63, 0.15)',
          borderLeft: `3px solid ${feedback.isCorrect ? theme.success : theme.error}`,
        }}>
          <p style={{
            fontWeight: '600',
            fontSize: '14px',
            color: feedback.isCorrect ? theme.success : theme.error,
            margin: 0,
          }}>
            {feedback.isCorrect ? '✓ Correct!' : '✗ Incorrect. ' + feedback.correctOption + ' is right'}
          </p>
        </div>
      )}

      {/* Next button */}
      <button
        onClick={handleNext}
        disabled={!showResult}
        style={{
          marginTop: '0.75rem',
          padding: '0.7rem 1rem',
          background: showResult ? theme.gold : '#1d2c47',
          color: showResult ? theme.bgDark : 'rgba(240, 237, 230, 0.25)',
          border: 'none',
          borderRadius: '4px',
          fontSize: '15px',
          cursor: showResult ? 'pointer' : 'not-allowed',
          width: '100%',
          fontWeight: '600',
          transition: 'all 0.2s',
        }}
      >
        {checking
          ? 'Checking...'
          : !showResult
          ? '← Select an answer first'
          : current + 1 >= questions.length
          ? 'See Results →'
          : 'Next Question →'}
      </button>

    </div>
  );
}

export default Quiz;
